'use client'

import { AxisBottom, AxisLeft } from '@visx/axis'
import { curveMonotoneX } from '@visx/curve'
import { GridRows } from '@visx/grid'
import { Group } from '@visx/group'
import { scaleLinear, scaleTime } from '@visx/scale'
import { LinePath, AreaClosed } from '@visx/shape'
import { localPoint } from '@visx/event'
import { TooltipWithBounds, withTooltip } from '@visx/tooltip'
import { WithTooltipProvidedProps } from '@visx/tooltip/lib/enhancers/withTooltip'
import { extent, max, minIndex } from 'd3-array'
import { MouseEvent, TouchEvent, useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { Circle } from 'lucide-react'
import { MarketAssetStatus } from '@/domain/market-info/reserve-status'

// Define chart colors
export const colors = {
  tooltipLine: 'hsla(0, 0%, 100%, 0.7)',
  backgroundLine: 'hsla(0, 0%, 100%, 0.3)',
  axisTickLabel: 'hsla(0, 0%, 100%, 0.5)',
  borrowAPY: 'hsl(161 94% 81%)',
  lendAPY: '#FCE572',
}

// Define chart margins
export const margins = { top: 40, right: 20, bottom: 30, left: 50 }

export function getPanelTitle(borrowStatus: MarketAssetStatus, supplyStatus: MarketAssetStatus): string {
  const showBorrowAPY = borrowStatus !== 'no'
  const showLendAPY = supplyStatus !== 'no'

  if (showBorrowAPY && showLendAPY) {
    return 'Historic Interest Rate'
  }

  if (showBorrowAPY) {
    return 'Borrow Rate'
  }

  if (showLendAPY) {
    return 'Supply Rate'
  }

  return 'Historic Interest Rate'
}

// Custom formatter for interest rate Y-axis ticks
export function formatInterestRateTick(value: any): string {
  const numValue = typeof value === 'number' ? value : Number(value)
  return `${numValue.toFixed(2)}%`
}

// Average Label Component
function AverageLabel({ x, y, value }: { x: number; y: number; value: number }) {
  return (
    <foreignObject x={x} y={y} width={65} height={25}>
      <div>
        <div
          style={{
            backgroundColor: 'rgba(20, 20, 20, 0.6)',
            color: 'white',
            padding: '3px 6px',
            position: 'absolute',
            fontSize: '10px',
            borderRadius: '4px',
            pointerEvents: 'none',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            zIndex: 5,
          }}
        >
          Avg {value.toFixed(2)}%
        </div>
      </div>
    </foreignObject>
  )
}

export interface ChartDataPoint {
  date: Date
  borrowAPY: number
  lendAPY: number
}

interface APYChartProps {
  width: number
  height: number
  data: ChartDataPoint[]
  supplyStatus: MarketAssetStatus
  borrowStatus: MarketAssetStatus
  showTime?: boolean
  showBorrowAvg?: boolean
  showLendAvg?: boolean
  customBorrowTitle?: string
  customLendTitle?: string
}

function APYChart({
  width,
  height,
  data,
  supplyStatus,
  borrowStatus,
  showTime = false,
  showBorrowAvg = true,
  showLendAvg = false,
  showTooltip,
  hideTooltip,
  tooltipData,
  tooltipLeft = 0,
  customBorrowTitle = 'Borrow APY',
  customLendTitle = 'Lend APY',
}: APYChartProps & WithTooltipProvidedProps<ChartDataPoint>) {
  // Animation states
  const [isVisible, setIsVisible] = useState(false)
  const [startLineAnimation, setStartLineAnimation] = useState(false)
  const [borrowPathLength, setBorrowPathLength] = useState(0)
  const [lendPathLength, setLendPathLength] = useState(0)

  // Refs for path elements
  const borrowPathRef = useRef<SVGPathElement>(null)
  const lendPathRef = useRef<SVGPathElement>(null)

  // Check if statuses allow displaying the lines
  const showBorrowAPY = borrowStatus !== 'no'
  const showLendAPY = supplyStatus !== 'no'

  // Function to calculate path lengths with proper timing
  const calculatePathLengths = useCallback(() => {
    requestAnimationFrame(() => {
      if (borrowPathRef.current && showBorrowAPY) {
        const length = borrowPathRef.current.getTotalLength()
        setBorrowPathLength(length)
      }
      if (lendPathRef.current && showLendAPY) {
        const length = lendPathRef.current.getTotalLength()
        setLendPathLength(length)
      }
    })
  }, [showBorrowAPY, showLendAPY])

  // Trigger animations after component mounts
  useEffect(() => {
    // Calculate path lengths after a short delay to ensure DOM is ready
    const pathLengthTimer = setTimeout(calculatePathLengths, 50)

    // First show the chart
    const initialTimer = setTimeout(() => {
      setIsVisible(true)
    }, 50)

    // Start line drawing animation after ensuring path lengths are calculated
    const lineAnimationTimer = setTimeout(() => {
      setStartLineAnimation(true)
    }, 150)

    return () => {
      clearTimeout(pathLengthTimer)
      clearTimeout(initialTimer)
      clearTimeout(lineAnimationTimer)
    }
  }, [calculatePathLengths])

  // Recalculate path lengths when data changes
  useEffect(() => {
    if (data.length > 0) {
      // Use a longer delay when data changes to ensure re-rendering is complete
      const timer = setTimeout(calculatePathLengths, 100)
      return () => clearTimeout(timer)
    }
  }, [data, calculatePathLengths])

  // Additional effect to ensure path lengths are calculated after paths are rendered
  useEffect(() => {
    if ((showBorrowAPY || showLendAPY) && data.length > 0) {
      // Multiple attempts to ensure we get the correct path length
      const timer1 = setTimeout(() => {
        if (borrowPathRef.current && showBorrowAPY) {
          const length = borrowPathRef.current.getTotalLength()
          if (length > 0) {
            setBorrowPathLength(length)
          }
        }
        if (lendPathRef.current && showLendAPY) {
          const length = lendPathRef.current.getTotalLength()
          if (length > 0) {
            setLendPathLength(length)
          }
        }
      }, 50)

      const timer2 = setTimeout(() => {
        if (borrowPathRef.current && showBorrowAPY) {
          const length = borrowPathRef.current.getTotalLength()
          if (length > 0) {
            setBorrowPathLength(length)
          }
        }
        if (lendPathRef.current && showLendAPY) {
          const length = lendPathRef.current.getTotalLength()
          if (length > 0) {
            setLendPathLength(length)
          }
        }
      }, 200)

      return () => {
        clearTimeout(timer1)
        clearTimeout(timer2)
      }
    }
  }, [data, showBorrowAPY, showLendAPY])

  const innerWidth = Math.max(0, width - margins.left - margins.right)
  const innerHeight = height - margins.top - margins.bottom

  // Calculate average borrow APY
  const avgBorrowAPY = useMemo(() => {
    if (!data.length) return 0
    const sum = data.reduce((acc, point) => acc + point.borrowAPY, 0)
    return sum / data.length
  }, [data])

  // Calculate average lend APY
  const avgLendAPY = useMemo(() => {
    if (!data.length) return 0
    const sum = data.reduce((acc, point) => acc + point.lendAPY, 0)
    return sum / data.length
  }, [data])

  // Create scales for X and Y axes
  const xScale = scaleTime({
    range: [0, innerWidth],
    domain: extent(data, (d) => d.date) as [Date, Date],
    nice: false,
  })

  const yScale = scaleLinear({
    range: [innerHeight, 0],
    domain: [0, Math.max((max(data, (d) => Math.max(d.borrowAPY, d.lendAPY)) || 0) * 1.2, 1)],
    nice: true,
  })

  // Handle tooltip display
  function handleTooltip(event: TouchEvent<SVGRectElement> | MouseEvent<SVGRectElement>): void {
    const point = localPoint(event) || { x: 0 }
    const x = point.x - margins.left
    const xDate = xScale.invert(x)
    const closestIndex = minIndex(data, (d) => Math.abs(d.date.getTime() - xDate.getTime()))
    const closestDataPoint = data[closestIndex]

    showTooltip({
      tooltipData: closestDataPoint,
      tooltipLeft: closestDataPoint ? xScale(closestDataPoint.date) + margins.left : undefined,
    })
  }

  return (
    <>
      <svg width={width} height={height}>
        <Group left={margins.left} top={margins.top}>
          <defs>
            <linearGradient id="borrow-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={colors.borrowAPY} stopOpacity={0.4} />
              <stop offset="40%" stopColor={colors.borrowAPY} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="lend-gradient" x1="0%" y1="0%" x2="0%" y2="150%">
              <stop offset="0%" stopColor={colors.lendAPY} stopOpacity={0.4} />
              <stop offset="60%" stopColor={colors.lendAPY} stopOpacity={0} />
            </linearGradient>
          </defs>

          {/* Horizontal y-axis grid lines */}
          <GridRows scale={yScale} width={innerWidth} stroke="rgba(255, 255, 255, 0.05)" strokeWidth={1} numTicks={6} />

          {/* Borrow APY Area (gradient fill) */}
          {showBorrowAPY && (
            <AreaClosed
              data={data}
              x={(d) => xScale(d.date)}
              y={(d) => yScale(d.borrowAPY)}
              yScale={yScale}
              fill="url(#borrow-gradient)"
              curve={curveMonotoneX}
              opacity={isVisible ? 0.4 : 0}
              style={{
                transition: 'opacity 1.2s ease-in-out',
                transitionDelay: '0s',
              }}
            />
          )}

          {/* Lend APY Area (gradient fill) */}
          {showLendAPY && (
            <AreaClosed
              data={data}
              x={(d) => xScale(d.date)}
              y={(d) => yScale(d.lendAPY)}
              yScale={yScale}
              fill="url(#lend-gradient)"
              curve={curveMonotoneX}
              opacity={isVisible ? 0.4 : 0}
              style={{
                transition: 'opacity 1.2s ease-in-out',
                transitionDelay: '0s',
              }}
            />
          )}

          {/* Borrow APY Line */}
          {showBorrowAPY && (
            <LinePath
              ref={borrowPathRef}
              data={data}
              x={(d) => xScale(d.date)}
              y={(d) => yScale(d.borrowAPY)}
              stroke={colors.borrowAPY}
              strokeWidth={1.5}
              curve={curveMonotoneX}
              opacity={isVisible ? 1 : 0}
              style={{
                transition: 'opacity 0.3s ease-out',
                strokeDasharray: borrowPathLength || 0,
                strokeDashoffset: startLineAnimation ? 0 : borrowPathLength || 0,
                transitionProperty: 'opacity, stroke-dashoffset',
                transitionDuration: '0.3s, 2s',
                transitionTimingFunction: 'ease-out, cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: '0s, 0s',
              }}
            />
          )}

          {/* Lend APY Line */}
          {showLendAPY && (
            <LinePath
              ref={lendPathRef}
              data={data}
              x={(d) => xScale(d.date)}
              y={(d) => yScale(d.lendAPY)}
              stroke={colors.lendAPY}
              strokeWidth={1.5}
              curve={curveMonotoneX}
              opacity={isVisible ? 1 : 0}
              style={{
                transition: 'opacity 0.3s ease-out',
                strokeDasharray: lendPathLength || 0,
                strokeDashoffset: startLineAnimation ? 0 : lendPathLength || 0,
                transitionProperty: 'opacity, stroke-dashoffset',
                transitionDuration: '0.3s, 2s',
                transitionTimingFunction: 'ease-out, cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: '0s, 0s',
              }}
            />
          )}

          {/* X-axis (Time) */}
          <AxisBottom
            top={innerHeight}
            scale={xScale}
            numTicks={Math.min(data.length, 5)}
            strokeWidth={0}
            tickLabelProps={() => ({
              fill: colors.axisTickLabel,
              fontSize: 10,
              textAnchor: 'middle',
              dy: 4,
            })}
          />

          {/* Y-axis (Interest Rate) */}
          <AxisLeft
            scale={yScale}
            numTicks={5}
            strokeWidth={0}
            tickFormat={formatInterestRateTick}
            tickLabelProps={() => ({
              fill: colors.axisTickLabel,
              fontSize: 10,
              textAnchor: 'end',
              dx: -8,
              dy: 3,
            })}
          />

          {/* Overlay for tooltip */}
          <rect
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onTouchStart={handleTooltip}
            onTouchMove={handleTooltip}
            onMouseMove={handleTooltip}
            onMouseLeave={hideTooltip}
          />

          {/* Average Borrow APY dotted line - positioned after lines to appear on top */}
          {isVisible && showBorrowAPY && showBorrowAvg && (
            <>
              <line
                x1={0}
                y1={yScale(avgBorrowAPY)}
                x2={innerWidth}
                y2={yScale(avgBorrowAPY)}
                stroke="rgba(255, 255, 255, 0.5)"
                strokeWidth={1.5}
                strokeDasharray="4,4"
                opacity={0.8}
                style={{
                  transition: 'opacity 0.5s ease-in',
                }}
              />

              <AverageLabel x={0} y={yScale(avgBorrowAPY) - 30} value={avgBorrowAPY} />
            </>
          )}

          {/* Average Lend APY dotted line */}
          {isVisible && showLendAPY && showLendAvg && (
            <>
              <line
                x1={0}
                y1={yScale(avgLendAPY)}
                x2={innerWidth}
                y2={yScale(avgLendAPY)}
                stroke="rgba(255, 255, 255, 0.5)"
                strokeWidth={1}
                strokeDasharray="4,4"
                opacity={0.8}
                style={{
                  transition: 'opacity 0.5s ease-in',
                }}
              />

              <AverageLabel x={0} y={yScale(avgLendAPY) - 30} value={avgLendAPY} />
            </>
          )}

          {/* Tooltip hover dots - positioned after all other elements to ensure they're on top */}
          {tooltipData && (
            <>
              {/* Borrow APY dot */}
              {showBorrowAPY && (
                <circle
                  cx={xScale(tooltipData.date)}
                  cy={yScale(tooltipData.borrowAPY)}
                  r={5}
                  fill={colors.borrowAPY}
                  pointerEvents="none"
                  style={{
                    filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.5))',
                    transition: 'all 0.1s ease-out',
                  }}
                />
              )}

              {/* Lend APY dot */}
              {showLendAPY && (
                <circle
                  cx={xScale(tooltipData.date)}
                  cy={yScale(tooltipData.lendAPY)}
                  r={5}
                  fill={colors.lendAPY}
                  pointerEvents="none"
                  style={{
                    filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.5))',
                    transition: 'all 0.1s ease-out',
                  }}
                />
              )}
            </>
          )}
        </Group>
      </svg>

      {tooltipData && (
        <div>
          <TooltipWithBounds
            key={Math.random()}
            top={
              margins.top +
              yScale(Math.max(showBorrowAPY ? tooltipData.borrowAPY : 0, showLendAPY ? tooltipData.lendAPY : 0)) -
              30
            }
            left={tooltipLeft}
            style={{
              backgroundColor: 'rgba(20, 20, 20, 0.9)',
              color: 'white',
              padding: '10px',
              fontSize: '12px',
              borderRadius: '8px',
              zIndex: 999,
              pointerEvents: 'none',
              position: 'absolute',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            }}
          >
            <div>
              <div>
                {tooltipData.date.toLocaleDateString()}
                {showTime && ` ${tooltipData.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
              </div>

              {showBorrowAPY && (
                <div className="flex justify-between gap-1.5">
                  <div className="flex items-center gap-1.5">
                    <Circle size={8} fill={colors.borrowAPY} stroke="0" />
                    <div className="text-muted-foreground text-xs">{customBorrowTitle}</div>
                  </div>
                  <div>{tooltipData.borrowAPY.toFixed(2)}%</div>
                </div>
              )}

              {showLendAPY && (
                <div className="flex justify-between gap-1.5">
                  <div className="flex items-center gap-1.5">
                    <Circle size={8} fill={colors.lendAPY} stroke="0" />
                    <div className="text-muted-foreground text-xs">{customLendTitle}</div>
                  </div>
                  <div>{tooltipData.lendAPY.toFixed(2)}%</div>
                </div>
              )}
            </div>
          </TooltipWithBounds>
        </div>
      )}
    </>
  )
}

// Export the chart with tooltip functionality
export const APYChartWithTooltip = withTooltip<APYChartProps, ChartDataPoint>(APYChart)
